"use client";

import { useSyncExternalStore } from "react";

/** Sticky "return needs a PIN" lock, shared across same-origin console tabs.
 *
 *  When staff hand a patient an in-person form (in its own tab, so a scribe on
 *  the launching tab keeps recording), that tab REGISTERS a form session. While
 *  any session is active, every other console tab covers itself with a PIN gate.
 *  The lock is deliberately sticky: closing or exiting a form tab does NOT end
 *  its session, so a patient can't escape the gate just by closing the form.
 *
 *  Each session gets its OWN localStorage key (`<PREFIX><id>`) rather than a
 *  shared list, so two overlapping form tabs never race: registering or ending a
 *  session is a single atomic setItem/removeItem that can't clobber another tab's
 *  session mid read-modify-write. Finishing one form (Done) removes only THAT
 *  key and leaves the lock up while another patient is still holding a form. A
 *  correct PIN on the other-tab return gate, or a full sign-out, ends every
 *  session at once.
 *
 *  localStorage is the single source of truth. It is shared across same-origin
 *  tabs and its `storage` event notifies the others instantly, so a reader tab
 *  always has a definitive answer. Every uncertainty fails CLOSED: registering
 *  REQUIRES persistence (if storage can't persist the session the launcher blocks
 *  the form rather than hand over an ungated chart), and if a read throws we
 *  assume LOCKED rather than risk exposing the EMR. There is deliberately no
 *  "we couldn't tell" state that could fail open. */

// One key per session: "decoda:inperson-lock:<id>". The bare prefix (no colon)
// is what the `storage` event and cleanup match on.
const KEY_NAMESPACE = "decoda:inperson-lock";
const KEY_PREFIX = `${KEY_NAMESPACE}:`;
// Same-tab storage writes don't fire the `storage` event (that fires only in
// other tabs), so we dispatch this custom event for the current tab.
const LOCAL_EVENT = "decoda:inperson-lock-change";

/** Scan the active session ids from storage. Throws if storage is unreadable —
 *  callers decide how to treat that (isInPersonLocked fails closed). */
function scanSessions(): Set<string> {
  const set = new Set<string>();
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key && key.startsWith(KEY_PREFIX)) {
      set.add(key.slice(KEY_PREFIX.length));
    }
  }
  return set;
}

function notifyLocal() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(LOCAL_EVENT));
  }
}

/** Register this tab's form session, arming the shared lock. Returns false when
 *  it could not be persisted, so the caller must block the flow — without
 *  persistence the other tabs can't be gated. */
export function registerFormSession(id: string): boolean {
  try {
    window.localStorage.setItem(KEY_PREFIX + id, "1");
  } catch {
    return false;
  }
  notifyLocal();
  return true;
}

/** End a single form session (Done on that form's tab). Removes only this
 *  session's key: the lock stays up if another form is still open. Returns false
 *  when storage refused the removal (the key is still present and other tabs stay
 *  gated), so the caller can keep the gate up and retry rather than let one tab
 *  through while the rest stay stuck. */
export function endFormSession(id: string): boolean {
  try {
    window.localStorage.removeItem(KEY_PREFIX + id);
  } catch {
    return false;
  }
  notifyLocal();
  return true;
}

/** End every form session at once — the staff member has reclaimed the device
 *  (a correct PIN on the return gate) or signed out. Returns false if storage
 *  refused a removal, so the lock stays and the caller can surface/retry. */
export function endAllFormSessions(): boolean {
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key && (key === KEY_NAMESPACE || key.startsWith(KEY_PREFIX))) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.localStorage.removeItem(key));
  } catch {
    return false;
  }
  notifyLocal();
  return true;
}

export function isInPersonLocked(): boolean {
  try {
    return scanSessions().size > 0;
  } catch {
    // Storage is unreadable: fail CLOSED. We can't tell whether a session is
    // active, and this gate protects PHI, so we keep the console covered rather
    // than risk exposing the EMR. A wholly unreadable localStorage also breaks
    // Firebase auth and the app's stores, so the console isn't usable anyway; the
    // "Sign out" link on the gate (which never reads localStorage) is the escape.
    return true;
  }
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (e: StorageEvent) => {
    // key === null is a storage.clear(); otherwise match any of our keys.
    if (e.key === null || e.key.startsWith(KEY_NAMESPACE)) onChange();
  };
  window.addEventListener("storage", onStorage);
  window.addEventListener(LOCAL_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener(LOCAL_EVENT, onChange);
  };
}

/** True while any return-PIN lock is armed. Uses useSyncExternalStore so the
 *  storage read is hydration-safe: the server snapshot is a stable `false` and
 *  the client reconciles to the real storage value without a mismatch. */
export function useInPersonLocked(): boolean {
  return useSyncExternalStore(subscribe, isInPersonLocked, () => false);
}
